'use client'

import { useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'

/* ─── Panel dot matrix ───────────────────────────────────────────────────
   Canvas layer over each colour panel: a quiet grid of dots where a few at
   a time swell bright and fade back (sharpened sine per dot, random
   phase/speed, so pulses stay sparse and unsynchronised). Runs only while
   the canvas is on screen and the tab is visible; reduced motion gets the
   static grid with no animation loop.

   Ink follows the canvas's CSS `color` (default white), so callers can
   theme it — e.g. `className="text-zinc-800 dark:text-white"` for panels
   that are light in light mode. A MutationObserver re-reads the ink when
   the html class flips theme. ── */
export const PanelDots = ({ className }: { className?: string }) => {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    const dpr = Math.min(window.devicePixelRatio || 1, 2)

    const GAP = 24 // grid spacing (px)
    const R = 1.2 // dot radius (px)
    const BASE = 0.07 // resting alpha
    const PEAK = 0.28 // extra alpha at full pulse

    let dots: { x: number; y: number; phase: number; speed: number }[] = []
    let raf = 0
    let last = 0
    let running = false
    let inView = false
    let ink = '#fff'
    // Layout size, captured once per resize. The animation loop must never
    // touch clientWidth/clientHeight: while a carousel drags this canvas,
    // every frame has just-invalidated styles, so a layout read here forces
    // a synchronous reflow per canvas per frame and the drag visibly drops
    // frames.
    let w = 0
    let h = 0

    // The resting grid, pre-rendered once. A frame is then one drawImage
    // plus only the dots currently pulsing (a handful), instead of
    // re-pathing every dot in the grid at its resting alpha each time.
    const base = document.createElement('canvas')
    const bctx = base.getContext('2d')

    const readInk = () => {
      ink = getComputedStyle(canvas).color || '#fff'
    }

    const drawBase = () => {
      if (!bctx) return
      base.width = canvas.width
      base.height = canvas.height
      bctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      bctx.clearRect(0, 0, w, h)
      bctx.globalAlpha = BASE
      bctx.fillStyle = ink
      for (const d of dots) {
        bctx.beginPath()
        bctx.arc(d.x, d.y, R, 0, Math.PI * 2)
        bctx.fill()
      }
      bctx.globalAlpha = 1
    }

    const blitBase = () => {
      ctx.clearRect(0, 0, w, h)
      // ctx carries the dpr transform, so drawing at the CSS size maps the
      // base's backing pixels 1:1 onto the visible canvas.
      ctx.drawImage(base, 0, 0, w, h)
    }

    const build = () => {
      w = canvas.clientWidth
      h = canvas.clientHeight
      // Round the backing store: a fractional assignment truncates, and the
      // resulting sub-pixel stretch smears the bitmap's edge into a visible
      // hairline where the panel meets its neighbour.
      canvas.width = Math.max(1, Math.round(w * dpr))
      canvas.height = Math.max(1, Math.round(h * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      readInk()
      dots = []
      for (let y = GAP / 2; y < h; y += GAP) {
        for (let x = GAP / 2; x < w; x += GAP) {
          dots.push({
            x,
            y,
            phase: Math.random() * Math.PI * 2,
            speed: 0.3 + Math.random() * 0.5,
          })
        }
      }
      drawBase()
      blitBase()
    }

    const frame = (t: number) => {
      raf = requestAnimationFrame(frame)
      if (t - last < 33) return // ~30fps is plenty for a slow shimmer
      last = t
      const sec = t / 1000
      blitBase()
      ctx.fillStyle = ink
      for (const d of dots) {
        const s = Math.sin(sec * d.speed + d.phase)
        // pow sharpens the sine into a short pulse with a long quiet tail;
        // below s = 0.75 the pulse alpha (PEAK * s^12) is under 1% — invisible
        // over the base dot, so skip the draw entirely.
        if (s <= 0.75) continue
        ctx.globalAlpha = PEAK * s ** 12
        ctx.beginPath()
        ctx.arc(d.x, d.y, R, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.globalAlpha = 1
    }

    const start = () => {
      if (running || reduced || !inView || document.hidden) return
      running = true
      raf = requestAnimationFrame(frame)
    }
    const stop = () => {
      running = false
      cancelAnimationFrame(raf)
    }

    const ro = new ResizeObserver(build)
    ro.observe(canvas)
    const io = new IntersectionObserver(([e]) => {
      inView = !!e?.isIntersecting
      if (inView) start()
      else stop()
    })
    io.observe(canvas)
    const onVis = () => (document.hidden ? stop() : start())
    document.addEventListener('visibilitychange', onVis)

    // Theme flips swap the html class — re-read the ink and re-render the
    // base bitmap; if the loop is idle, also push it to the visible canvas
    // (a running loop blits the fresh base on its next frame).
    const mo = new MutationObserver(() => {
      readInk()
      drawBase()
      if (!running) blitBase()
    })
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })

    build()

    return () => {
      stop()
      ro.disconnect()
      io.disconnect()
      mo.disconnect()
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [])

  const EDGE_MASK =
    'linear-gradient(to right, transparent 0, #000 12px, #000 calc(100% - 12px), transparent 100%), ' +
    'linear-gradient(to bottom, transparent 0, #000 12px, #000 calc(100% - 12px), transparent 100%)'

  return (
    <canvas
      ref={ref}
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-0 size-full text-white',
        className
      )}
      // Alpha fades to zero before every edge, so however the compositor
      // snaps or resamples this layer during carousel motion, the bitmap
      // boundary itself is invisible. The dot grid already stops ~11px in,
      // so the mask changes nothing visually.
      style={{
        maskImage: EDGE_MASK,
        maskComposite: 'intersect',
        WebkitMaskImage: EDGE_MASK,
        WebkitMaskComposite: 'source-in',
      }}
    />
  )
}

export default PanelDots
